import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchMediaBlob, getMediaElementUrl } from '../../../../api/mediaFetch'
import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { createNadDebug } from '../../../../utils/debugLog'

const PEAK_COUNT = 96
/**
 * RFC-0651 #8 + RFC-0631 (Issue 5): thin bottom-anchored waveform style.
 * 1px bar / 1px gap, all bars the same color, growing UP from the bottom
 * edge of the container. RFC-0633 (Issue 2) adds a `variant` prop to
 * support both styles in parallel:
 *   - `modern` (default): the post-RFC-0630 thin bottom-anchored render.
 *     Used by `TimelineClip` (in-clip waveform overlay) and
 *     `TimelineAudioClip` (audio-track clip body).
 *   - `classic`: pre-RFC-0630 symmetric-around-middle style. 2px bar /
 *     1px gap, centered vertically. Used by `ClipPanel` clip-selector
 *     audio thumbnails.
 * Color in both variants is inherited from the parent via `currentColor`.
 */
const BAR_WIDTH_PX_MODERN = 1
const BAR_GAP_PX_MODERN = 1
const BAR_WIDTH_PX_CLASSIC = 2
const BAR_GAP_PX_CLASSIC = 1
const WAVEFORM_BURN_COLOR = '#ff2222'

interface WaveformPeaks {
  heights: number[]
  peakAmps: number[]
}

const waveformCache = new Map<string, WaveformPeaks>()

// RFC-0630 v3 (architect-review #4): emit a single structured log per
// successful waveform peaks decode so the new bar-style render path is
// observable in NAD traces. Mount-only `render-mounted` events are
// suppressed by amplitude-throttle inside the React tree to keep noise
// down — once-per-cacheKey is plenty.
const waveformStyleLog = createNadDebug('dw:video-editor:waveform-style')
const waveformStyleLogged = new Set<string>()

interface StaticWaveformProps {
  sourcePath: string
  barCount?: number
  amplitude?: number
  /** Volume gain applied to the clip (0-1). When peak * gain > 1, a burn mark appears. */
  gain?: number
  className?: string
  /** RFC-0633 (Issue 2): bar style. */
  variant?: 'modern' | 'classic'
}

function fallbackPeaks(key: string, count: number): WaveformPeaks {
  let seed = 0
  for (let i = 0; i < key.length; i += 1) {
    seed = (seed * 31 + key.charCodeAt(i)) >>> 0
  }
  const heights = Array.from({ length: count }, (_, i) => {
    seed = (seed * 1664525 + 1013904223 + i) >>> 0
    return 0.22 + (seed / 0xffffffff) * 0.68
  })
  return { heights, peakAmps: heights.map(() => 0) }
}

async function decodePeaks(projectId: string, sourcePath: string, cacheKey: string, count: number): Promise<WaveformPeaks> {
  const cached = waveformCache.get(cacheKey)
  if (cached) return cached

  const response = await fetchMediaBlob(projectId, sourcePath)
  if (!response.ok) {
    throw new Error(`Waveform fetch failed: ${response.status}`)
  }
  const arrayBuffer = await response.arrayBuffer()
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext
  const audioContext = new AudioContextCtor()
  try {
    const buffer = await audioContext.decodeAudioData(arrayBuffer.slice(0))
    const channel = buffer.getChannelData(0)
    const samplesPerPeak = Math.max(1, Math.floor(channel.length / count))
    const heights: number[] = []
    const peakAmps: number[] = []
    for (let index = 0; index < count; index++) {
      const start = index * samplesPerPeak
      const end = Math.min(channel.length, start + samplesPerPeak)
      let sum = 0
      let maxAbs = 0
      for (let i = start; i < end; i += 1) {
        const abs = Math.abs(channel[i])
        sum += abs
        if (abs > maxAbs) maxAbs = abs
      }
      heights.push(Math.min(1, Math.max(0.08, (sum / Math.max(1, end - start)) * 4)))
      peakAmps.push(maxAbs)
    }
    const result: WaveformPeaks = { heights, peakAmps }
    waveformCache.set(cacheKey, result)
    return result
  } finally {
    void audioContext.close()
  }
}

export function StaticWaveform({
  sourcePath,
  barCount = PEAK_COUNT,
  amplitude = 1,
  gain,
  className = '',
  variant = 'modern',
}: StaticWaveformProps) {
  const projectId = useProjectId()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [measuredBarCount, setMeasuredBarCount] = useState(barCount)
  const effectiveBarCount = Math.max(barCount, measuredBarCount)
  const cacheKey = `${sourcePath}:${effectiveBarCount}`
  const barWidthPx = variant === 'classic' ? BAR_WIDTH_PX_CLASSIC : BAR_WIDTH_PX_MODERN
  const barGapPx = variant === 'classic' ? BAR_GAP_PX_CLASSIC : BAR_GAP_PX_MODERN
  const [waveform, setWaveform] = useState<WaveformPeaks>(() => {
    const cached = waveformCache.get(cacheKey)
    return cached ?? fallbackPeaks(cacheKey, effectiveBarCount)
  })

  const sourceUrl = useMemo(() => {
    if (!projectId || !sourcePath) return null
    return getMediaElementUrl(projectId, sourcePath)
  }, [projectId, sourcePath])

  useEffect(() => {
    if (!projectId || !sourceUrl || !sourcePath) return
    let cancelled = false
    decodePeaks(projectId, sourcePath, cacheKey, effectiveBarCount)
      .then((decoded) => {
        if (!cancelled) setWaveform(decoded)
      })
      .catch(() => {
        const fallback = fallbackPeaks(cacheKey, effectiveBarCount)
        waveformCache.set(cacheKey, fallback)
        if (!cancelled) setWaveform(fallback)
      })
    return () => {
      cancelled = true
    }
  }, [projectId, sourceUrl, sourcePath, cacheKey, effectiveBarCount])

  useEffect(() => {
    const element = containerRef.current
    if (!element || typeof ResizeObserver === 'undefined') return

    const updateCount = () => {
      const width = element.clientWidth
      const count = Math.max(1, Math.floor(width / (barWidthPx + barGapPx)))
      setMeasuredBarCount(count)
    }

    updateCount()
    const observer = new ResizeObserver(updateCount)
    observer.observe(element)
    return () => observer.disconnect()
  }, [barWidthPx, barGapPx])

  useEffect(() => {
    if (waveform.heights.length === 0) return
    if (waveformStyleLogged.has(cacheKey)) return
    waveformStyleLogged.add(cacheKey)
    // RFC-0631 round-3 (Issue 2): resolve the inherited text color of the
    // container at mount time so NAD traces can confirm the bars actually
    // render in amber (not the default white from no `text-*` parent).
    // RFC-0631 round-3 (Issue 2): resolve the inherited text color of the
    // container at mount time so NAD traces can confirm the bars actually
    // render in amber (not the default white from no `text-*` parent).
    let resolvedColor: string | null = null
    try {
      const el = containerRef.current
      if (el) {
        resolvedColor = getComputedStyle(el).color
      }
    } catch {
      // best-effort
    }
    waveformStyleLog({
      event: 'thin-bar-rendered',
      cacheKey,
      barCount: waveform.heights.length,
      barWidthPx,
      barGapPx,
      anchor: variant === 'classic' ? 'center' : 'bottom',
      variant,
    })
    waveformStyleLog({
      event: 'waveform-color-resolved',
      cacheKey,
      color: resolvedColor,
    })
  }, [cacheKey, waveform, barGapPx, barWidthPx, variant])

  return (
    <div
      ref={containerRef}
      className={`flex h-full w-full ${variant === 'classic' ? 'items-center' : 'items-end'} justify-start overflow-hidden ${className}`}
      style={{ gap: `${barGapPx}px` }}
      aria-hidden="true"
      data-testid="static-waveform"
      data-variant={variant}
    >
      {waveform.heights.map((peak, index) => {
        const heightPct = Math.max(8, peak * Math.max(0, Math.min(1, amplitude)) * 100)
        const isBurning = gain !== undefined && waveform.peakAmps[index] * gain > 1
        return (
          <div
            key={`${sourcePath}-${index}`}
            className={`${variant === 'classic' ? 'shrink-0 self-center' : 'shrink-0'} relative`}
            style={{
              width: `${barWidthPx}px`,
              height: `${heightPct}%`,
              backgroundColor: 'currentColor',
              borderRadius: 0,
            }}
          >
            {isBurning && (
              <div
                className="absolute left-0 right-0 top-0"
                style={{
                  height: `${Math.max(2, barWidthPx * 2)}px`,
                  backgroundColor: WAVEFORM_BURN_COLOR,
                }}
                data-burn="true"
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

declare global {
  interface Window {
    webkitAudioContext?: typeof AudioContext
  }
}
